import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { skillApi, type Skill } from '@/lib/api';
import { useNotification } from '@/contexts/NotificationContext';
import {
  ArrowLeft, BookOpen, Webhook, Code2, Zap, Save, ShieldCheck,
  Shield, Users, AlertTriangle, CheckCircle2, XCircle,
  Globe, Cpu, Play, ChevronRight, RefreshCw, CheckSquare, Settings2,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/skills/$skillId')({
  component: SkillDetailPage,
});

// ── Constants ─────────────────────────────────────────────────────────────

const IMPL_META: Record<string, { label: string; icon: React.ReactNode; color: string; desc: string }> = {
  KNOWLEDGE_RETRIEVAL: { label: 'Knowledge Retrieval', icon: <BookOpen size={16} />, color: '#06b6d4', desc: 'Searches a knowledge base and returns relevant chunks to the LLM context.' },
  EXTERNAL_API:        { label: 'External API',        icon: <Webhook  size={16} />, color: '#8b5cf6', desc: 'Calls an external HTTP endpoint and passes the response back to the agent.' },
  CODE:                { label: 'Code Execution',      icon: <Code2    size={16} />, color: '#f59e0b', desc: 'Runs a sandboxed code snippet and returns the output.' },
  PROMPT_ONLY:         { label: 'Prompt-Only',         icon: <Zap      size={16} />, color: '#10b981', desc: 'Injects instructions into the system prompt; no tool call is made.' },
};

const RISK_META: Record<string, { color: string; bg: string; icon: React.ReactNode; desc: string }> = {
  LOW:      { color: '#10b981', bg: '#10b98112', icon: <CheckCircle2 size={12} />, desc: 'Safe for autonomous execution.' },
  MEDIUM:   { color: '#f59e0b', bg: '#f59e0b12', icon: <AlertTriangle size={12} />, desc: 'Use with caution; consider enabling approval.' },
  HIGH:     { color: '#ef4444', bg: '#ef444412', icon: <XCircle size={12} />, desc: 'Human approval strongly recommended.' },
  CRITICAL: { color: '#dc2626', bg: '#dc262612', icon: <XCircle size={12} />, desc: 'Always requires human approval before execution.' },
};

type DetailTab = 'overview' | 'configuration' | 'schema' | 'agents';

// ── JSON Editor ───────────────────────────────────────────────────────────

function JsonEditor({ value, onChange, label, placeholder }: {
  value: object | undefined;
  onChange: (v: object) => void;
  label: string;
  placeholder?: string;
}) {
  const [text, setText] = useState(JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);

  const handleChange = (raw: string) => {
    setText(raw);
    try {
      const parsed = JSON.parse(raw);
      setError(null);
      onChange(parsed);
    } catch {
      setError('Invalid JSON');
    }
  };

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <textarea
        value={text}
        onChange={e => handleChange(e.target.value)}
        style={{
          ...inputStyle,
          fontFamily: '"Fira Code", "JetBrains Mono", monospace',
          fontSize: 12,
          resize: 'vertical',
          minHeight: 140,
          lineHeight: 1.6,
          background: '#0d0f1a',
          color: error ? '#ef4444' : '#a5f3fc',
          border: `1px solid ${error ? '#ef4444' : 'var(--border)'}`,
        }}
        spellCheck={false}
        placeholder={placeholder ?? '{}'}
      />
      {error && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 4, display: 'block' }}>⚠ {error}</span>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function SkillDetailPage() {
  const { skillId } = Route.useParams();
  const qc = useQueryClient();
  const { success, error: notifyError } = useNotification();

  const { data: skill, isLoading } = useQuery({
    queryKey: ['skill', skillId],
    queryFn: () => skillApi.get(skillId),
  });

  const [tab, setTab] = useState<DetailTab>('overview');
  const [form, setForm] = useState<Partial<Skill>>({});
  const [dirty, setDirty] = useState(false);
  const [testInput, setTestInput] = useState('{}');
  const [testResult, setTestResult] = useState<{ ok: boolean; data: unknown } | null>(null);
  const [testRunning, setTestRunning] = useState(false);

  useEffect(() => {
    if (skill) {
      setForm({
        name: skill.name,
        description: skill.description ?? '',
        implementationType: skill.implementationType,
        riskLevel: skill.riskLevel,
        approvalRequired: skill.approvalRequired,
        authRequirement: skill.authRequirement,
        toolName: skill.toolName ?? '',
        systemPromptInstructions: skill.systemPromptInstructions ?? '',
        maxExecutionsPerConversation: skill.maxExecutionsPerConversation,
        maxExecutionsPerHour: skill.maxExecutionsPerHour,
        inputSchema: skill.inputSchema,
        outputSchema: skill.outputSchema,
        skillConfig: skill.skillConfig,
        countryRestrictions: skill.countryRestrictions,
      });
      setDirty(false);
    }
  }, [skill]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Skill>) => skillApi.update(skillId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['skill', skillId] });
      qc.invalidateQueries({ queryKey: ['skills'] });
      success('Skill saved', 'Changes are live across all attached agents.');
      setDirty(false);
    },
    onError: (e) => notifyError('Save failed', (e as Error).message),
  });

  const set = <K extends keyof Skill>(k: K, v: Skill[K]) => {
    setForm(f => ({ ...f, [k]: v }));
    setDirty(true);
  };

  async function handleTestRun() {
    setTestRunning(true);
    setTestResult(null);
    try {
      const params = JSON.parse(testInput) as Record<string, unknown>;
      // Use toolApi.test if this is a tool-based skill, else simulate
      await new Promise(r => setTimeout(r, 900)); // simulate
      setTestResult({ ok: true, data: { message: 'Simulated success — configure a real tool to test live execution.', params, skillId, implementationType: skill?.implementationType } });
    } catch {
      setTestResult({ ok: false, data: 'Invalid JSON input' });
    } finally {
      setTestRunning(false);
    }
  }

  if (isLoading || !skill) {
    return (
      <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-primary)' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const impl = IMPL_META[skill.implementationType];
  const risk = RISK_META[skill.riskLevel];
  const savedRisk = RISK_META[form.riskLevel ?? skill.riskLevel];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── Sticky header ── */}
      <div style={{
        padding: '18px 28px 0',
        background: 'var(--surface-1)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-muted)', marginBottom: 14 }}>
          <Link to="/skills" style={{ color: 'var(--color-muted)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            <ArrowLeft size={13} /> Skills
          </Link>
          <ChevronRight size={12} />
          <span style={{ color: 'var(--color-text)' }}>{skill.name}</span>
        </div>

        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `${impl.color}18`, border: `1px solid ${impl.color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: impl.color,
            }}>
              {impl.icon}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{skill.name}</h1>
                <span style={{ fontSize: 10, fontWeight: 700, background: `${impl.color}18`, color: impl.color, border: `1px solid ${impl.color}30`, borderRadius: 5, padding: '2px 7px' }}>
                  {impl.label}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, background: risk.bg, color: risk.color, border: `1px solid ${risk.color}30`, borderRadius: 5, padding: '2px 7px' }}>
                  {risk.icon} {skill.riskLevel}
                </span>
                {skill.approvalRequired && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, background: '#6366f115', color: '#818cf8', border: '1px solid #6366f130', borderRadius: 5, padding: '2px 7px' }}>
                    <ShieldCheck size={9} /> APPROVAL REQUIRED
                  </span>
                )}
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', margin: '4px 0 0' }}>
                {skill._count?.agentConfigs ?? 0} agents using this skill · Created {new Date(skill.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Save button */}
          {dirty && (
            <button
              onClick={() => updateMutation.mutate(form)}
              disabled={updateMutation.isPending}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '10px 20px', borderRadius: 9, border: 'none',
                background: impl.color, color: '#fff',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                boxShadow: `0 4px 16px ${impl.color}35`,
                animation: 'pulse-save 1.5s ease infinite',
              }}
            >
              <Save size={14} /> {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0 }}>
          {([
            { id: 'overview',       label: 'Overview',        icon: <Cpu size={13} />,      count: undefined },
            { id: 'configuration',  label: 'Configuration',   icon: <Settings2 size={13} />, count: undefined },
            { id: 'schema',         label: 'Schema & Config', icon: <Code2 size={13} />,     count: undefined },
            { id: 'agents',         label: 'Agents',          icon: <Users size={13} />,     count: skill._count?.agentConfigs },
          ] as { id: string; label: string; icon: React.ReactNode; count: number | undefined }[]).map(t => (
            <button
              key={t.id}
              id={`skill-tab-${t.id}`}
              onClick={() => setTab(t.id as DetailTab)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
                color: tab === t.id ? impl.color : 'var(--color-muted)',
                borderBottom: `2px solid ${tab === t.id ? impl.color : 'transparent'}`,
                marginBottom: -1, transition: 'color 0.15s',
              }}
            >
              {t.icon} {t.label}
              {t.count != null && t.count > 0 && (
                <span style={{ fontSize: 10, background: impl.color + '25', color: impl.color, borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 28 }}>

        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Impl type card */}
            <InfoCard title="Skill Type" accent={impl.color}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ color: impl.color, width: 36, height: 36, background: impl.color + '18', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {impl.icon}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{impl.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 2 }}>{impl.desc}</div>
                </div>
              </div>
            </InfoCard>

            {/* Basic fields */}
            <InfoCard title="Identity">
              <Field label="Name">
                <input style={inputStyle} value={form.name ?? ''} onChange={e => set('name', e.target.value)} />
              </Field>
              <Field label="Description">
                <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.description ?? ''} onChange={e => set('description', e.target.value)} placeholder="Describe when and how the agent should use this skill…" />
              </Field>
            </InfoCard>

            {/* System prompt */}
            <InfoCard title="System Prompt Instructions" accent="#818cf8">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                This text is automatically injected into the agent's system prompt when the skill is attached.
                Instruct the LLM on <em>when</em> and <em>how</em> to invoke this skill.
              </p>
              <textarea
                id="system-prompt-instructions"
                style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                value={form.systemPromptInstructions ?? ''}
                onChange={e => set('systemPromptInstructions', e.target.value)}
                placeholder="When the user asks about [topic], use the search_knowledge skill to retrieve relevant information before responding."
              />
            </InfoCard>

            {/* Risk & Safety */}
            <InfoCard title="Risk & Safety" accent={savedRisk.color}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Risk Level">
                  <select style={inputStyle} value={form.riskLevel ?? 'LOW'} onChange={e => set('riskLevel', e.target.value as Skill['riskLevel'])}>
                    {Object.entries(RISK_META).map(([r, m]) => (
                      <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()} — {m.desc}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Auth Requirement">
                  <select style={inputStyle} value={form.authRequirement ?? 'NONE'} onChange={e => set('authRequirement', e.target.value as Skill['authRequirement'])}>
                    <option value="NONE">None</option>
                    <option value="OPTIONAL">Optional — use if available</option>
                    <option value="REQUIRED">Required — must authenticate</option>
                  </select>
                </Field>
              </div>

              {/* Approval toggle */}
              <div
                style={{
                  padding: '14px 16px', borderRadius: 10, marginTop: 8,
                  border: `1px solid ${form.approvalRequired ? '#6366f133' : 'var(--border)'}`,
                  background: form.approvalRequired ? '#6366f108' : 'var(--surface-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
                onClick={() => set('approvalRequired', !form.approvalRequired)}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: form.approvalRequired ? '#818cf8' : 'var(--color-text)' }}>
                    <ShieldCheck size={14} /> Requires human approval before execution
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 3 }}>
                    When enabled, the agent will pause and create an approval request before running this skill.
                  </p>
                </div>
                <Toggle on={!!form.approvalRequired} />
              </div>

              {(form.riskLevel === 'HIGH' || form.riskLevel === 'CRITICAL') && !form.approvalRequired && (
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: '#ef444412', border: '1px solid #ef444430', color: '#ef4444', fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>This skill has <strong>{form.riskLevel}</strong> risk but approval is disabled. Consider enabling approval for sensitive operations.</span>
                </div>
              )}
            </InfoCard>

            {/* Rate limits */}
            <InfoCard title="Rate Limits & Execution Controls" accent="#f59e0b">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Max executions per conversation">
                  <input type="number" style={inputStyle} min={1} placeholder="Unlimited"
                    value={form.maxExecutionsPerConversation ?? ''}
                    onChange={e => set('maxExecutionsPerConversation', e.target.value ? Number(e.target.value) : undefined)} />
                  <span style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4, display: 'block' }}>Prevent loops — agent won't invoke more than N times per session</span>
                </Field>
                <Field label="Max executions per hour">
                  <input type="number" style={inputStyle} min={1} placeholder="Unlimited"
                    value={form.maxExecutionsPerHour ?? ''}
                    onChange={e => set('maxExecutionsPerHour', e.target.value ? Number(e.target.value) : undefined)} />
                  <span style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4, display: 'block' }}>Global hourly cap across all conversations</span>
                </Field>
              </div>
            </InfoCard>
          </div>
        )}

        {/* CONFIGURATION TAB */}
        {tab === 'configuration' && (
          <div style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <InfoCard title="Tool Binding" accent={impl.color}>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 14 }}>
                {impl.label === 'Prompt-Only'
                  ? 'Prompt-only skills do not execute code. They inject instructions into the system prompt.'
                  : 'The tool name must match a registered executor. It\'s used by the orchestration engine to invoke the correct handler.'}
              </p>
              <Field label="Tool Name (function identifier)">
                <input
                  id="tool-name"
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                  placeholder="e.g. search_knowledge, send_email, lookup_order"
                  value={form.toolName ?? ''}
                  onChange={e => set('toolName', e.target.value)}
                  disabled={form.implementationType === 'PROMPT_ONLY'}
                />
              </Field>
            </InfoCard>

            <InfoCard title="Channel & Country Restrictions" accent="#06b6d4">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 14 }}>
                Restrict this skill to specific channels or geographies. Leave empty to allow everywhere.
              </p>
              <Field label="Country restrictions (comma-separated ISO codes)">
                <div style={{ position: 'relative' }}>
                  <Globe size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-muted)' }} />
                  <input
                    style={{ ...inputStyle, paddingLeft: 34 }}
                    placeholder="e.g. FI, SE, NO — or leave blank for all countries"
                    value={(form.countryRestrictions ?? []).join(', ')}
                    onChange={e => set('countryRestrictions', e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))}
                  />
                </div>
                {(form.countryRestrictions ?? []).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    {(form.countryRestrictions ?? []).map(c => (
                      <span key={c} style={{ fontSize: 11, background: '#06b6d418', color: '#06b6d4', border: '1px solid #06b6d430', borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>{c}</span>
                    ))}
                  </div>
                )}
              </Field>
            </InfoCard>
          </div>
        )}

        {/* SCHEMA TAB */}
        {tab === 'schema' && (
          <div style={{ maxWidth: 860, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <InfoCard title="Input Schema" accent="#06b6d4">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                JSON Schema describing what parameters the LLM should pass when invoking this skill. The orchestration engine validates inputs against this schema.
              </p>
              <JsonEditor
                label="inputSchema"
                value={form.inputSchema as object}
                onChange={v => { setForm(f => ({ ...f, inputSchema: v })); setDirty(true); }}
                placeholder={`{\n  "type": "object",\n  "properties": {\n    "query": { "type": "string", "description": "Search query" }\n  },\n  "required": ["query"]\n}`}
              />
            </InfoCard>

            <InfoCard title="Output Schema" accent="#10b981">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                JSON Schema describing the structure of the skill's output. Used for validation and documentation.
              </p>
              <JsonEditor
                label="outputSchema"
                value={form.outputSchema as object}
                onChange={v => { setForm(f => ({ ...f, outputSchema: v })); setDirty(true); }}
                placeholder={`{\n  "type": "object",\n  "properties": {\n    "results": { "type": "array" },\n    "total":   { "type": "number" }\n  }\n}`}
              />
            </InfoCard>

            <InfoCard title="Skill Config" accent="#f59e0b">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                Implementation-specific configuration (e.g. API endpoint, model ID, knowledge source ID). This is passed to the executor at runtime.
              </p>
              <JsonEditor
                label="skillConfig"
                value={form.skillConfig as object}
                onChange={v => { setForm(f => ({ ...f, skillConfig: v })); setDirty(true); }}
                placeholder={`{\n  "endpoint": "https://api.example.com/search",\n  "timeout": 5000\n}`}
              />
            </InfoCard>

            {/* Test runner */}
            <InfoCard title="Test Execution" accent="#8b5cf6">
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 12 }}>
                Run a test invocation of this skill with custom parameters to verify your schema and config.
              </p>
              <Field label="Input Parameters (JSON)">
                <textarea
                  style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12, minHeight: 80, color: '#a5f3fc', background: '#0d0f1a' }}
                  value={testInput}
                  onChange={e => setTestInput(e.target.value)}
                  placeholder='{"query": "example"}'
                />
              </Field>
              <button
                id="run-skill-test"
                onClick={handleTestRun}
                disabled={testRunning}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: '#8b5cf6', color: '#fff',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  opacity: testRunning ? 0.7 : 1,
                }}
              >
                {testRunning
                  ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
                  : <><Play size={13} /> Run Test</>}
              </button>
              {testResult && (
                <div style={{
                  marginTop: 14, padding: '12px 14px', borderRadius: 9,
                  background: testResult.ok ? '#10b98112' : '#ef444412',
                  border: `1px solid ${testResult.ok ? '#10b98130' : '#ef444430'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: testResult.ok ? '#10b981' : '#ef4444', marginBottom: 8 }}>
                    {testResult.ok ? <CheckSquare size={13} /> : <XCircle size={13} />}
                    {testResult.ok ? 'Test passed' : 'Test failed'}
                  </div>
                  <pre style={{ fontSize: 11, fontFamily: 'monospace', color: testResult.ok ? '#6ee7b7' : '#fca5a5', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(testResult.data, null, 2)}
                  </pre>
                </div>
              )}
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </InfoCard>
          </div>
        )}

        {/* AGENTS TAB */}
        {tab === 'agents' && (
          <div style={{ maxWidth: 720 }}>
            <InfoCard title={`Agents using this skill (${skill.agentConfigs?.length ?? 0})`} accent={impl.color}>
              {!skill.agentConfigs || skill.agentConfigs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-muted)' }}>
                  <Users size={28} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.3 }} />
                  <p style={{ fontWeight: 600 }}>No agents using this skill yet</p>
                  <p style={{ fontSize: 12, marginTop: 6 }}>Attach this skill to an agent from the Agent → Skills tab.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {skill.agentConfigs.map(cfg => (
                    <div key={cfg.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: impl.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: impl.color }}>
                          <Cpu size={14} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{cfg.agent?.name ?? 'Unknown agent'}</div>
                          <div style={{ fontSize: 11, color: 'var(--color-muted)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                            <span style={{ color: cfg.agent?.status === 'ACTIVE' ? '#10b981' : '#94a3b8' }}>● {cfg.agent?.status}</span>
                            {cfg.approvalRequired && <span style={{ color: '#818cf8', display: 'flex', alignItems: 'center', gap: 3 }}><Shield size={9} /> Per-agent approval</span>}
                            {!cfg.enabled && <span style={{ color: '#ef4444' }}>Disabled</span>}
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/agents/$agentId"
                        params={{ agentId: cfg.agentId }}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: impl.color, textDecoration: 'none', padding: '5px 12px', borderRadius: 7, background: impl.color + '18', border: `1px solid ${impl.color}30`, fontWeight: 600 }}
                      >
                        Open <ChevronRight size={11} />
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </InfoCard>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse-save {
          0%, 100% { box-shadow: 0 4px 16px ${impl.color}35; }
          50%       { box-shadow: 0 4px 28px ${impl.color}60; }
        }
      `}</style>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function InfoCard({ title, children, accent }: { title: string; children: React.ReactNode; accent?: string }) {
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: `1px solid ${accent ? accent + '22' : 'var(--border)'}`,
      borderRadius: 14, overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
        {accent && <div style={{ width: 3, height: 14, borderRadius: 2, background: accent }} />}
        <span style={{ fontSize: 12, fontWeight: 700, color: accent ?? 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      <div style={{ padding: '18px 18px' }}>{children}</div>
    </div>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <div style={{
      width: 40, height: 22, borderRadius: 11,
      background: on ? '#6366f1' : 'var(--surface-3)',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: on ? 21 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s',
      }} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-3)',
  color: 'var(--color-text)',
  fontSize: 13, width: '100%',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--color-muted)', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 6,
};
